import { Badge, GridCellText, type TableColumn, Tooltip } from '@civilgrid/ui/components';
import { OPERATOR_TYPES } from '@civilgrid/ui/utils';
import type { DataRefreshStatusRow } from '~/api/dataRefreshStatus';
import { getResearchStatusLabel, getResearchStatusVariant, RESEARCH_STATUS_LABELS } from '~/common/researchStatus';
import { formatUTCDate } from '~/pages/homeViewV2/utils/dates';

/**
 * Conflict dataset types served by the backend. Values must match `agencies.type` strings —
 * mirrors CONFLICT_AGENCY_TYPES in @civilgrid/db (a value import would pull server code into
 * the client bundle).
 */
const TYPE_OPTIONS = [
  { value: 'Paving Projects', label: 'Paving Projects' },
  { value: 'Moratoriums', label: 'Moratoriums' },
  { value: 'CIP', label: 'CIP' },
];

const RESEARCH_STATUS_OPTIONS = RESEARCH_STATUS_LABELS.map((label) => ({ value: label, label }));

export const columns: TableColumn<DataRefreshStatusRow>[] = [
  {
    id: 'name',
    accessorKey: 'name',
    header: 'Name',
    cell: (info) => <GridCellText>{info.getValue<DataRefreshStatusRow['name']>() || '—'}</GridCellText>,
    enableHiding: false,
    enableSorting: true,
    enableColumnFilter: true,
    size: 190,
    meta: {
      filterValueConfig: { type: 'text' },
      filterOperatorTypeConfig: OPERATOR_TYPES.TEXT,
    },
  },
  {
    id: 'type',
    accessorKey: 'type',
    header: 'Type',
    cell: (info) => <GridCellText>{info.getValue<DataRefreshStatusRow['type']>() || '—'}</GridCellText>,
    enableSorting: true,
    enableColumnFilter: true,
    size: 130,
    meta: {
      filterValueConfig: { type: 'select', options: TYPE_OPTIONS },
      filterOperatorTypeConfig: OPERATOR_TYPES.EXACT,
    },
  },
  {
    id: 'lastRefresh',
    accessorKey: 'lastRefresh',
    header: 'Last Refresh',
    cell: (info) => <GridCellText>{formatUTCDate(info.getValue<DataRefreshStatusRow['lastRefresh']>())}</GridCellText>,
    enableSorting: true,
    enableColumnFilter: true,
    size: 160,
    meta: {
      filterValueConfig: { type: 'date' },
      filterOperatorTypeConfig: OPERATOR_TYPES.DATE,
    },
  },
  {
    id: 'researchStatus',
    accessorKey: 'researchStatus',
    header: 'Research Status',
    cell: (info) => {
      const status = info.getValue<DataRefreshStatusRow['researchStatus']>() ?? '';
      if (!status) return <GridCellText>—</GridCellText>;
      return (
        <Badge color={getResearchStatusVariant(status)}>
          <GridCellText>{getResearchStatusLabel(status)}</GridCellText>
        </Badge>
      );
    },
    enableSorting: true,
    enableColumnFilter: true,
    size: 230,
    meta: {
      filterValueConfig: { type: 'select', options: RESEARCH_STATUS_OPTIONS },
      filterOperatorTypeConfig: OPERATOR_TYPES.EXACT,
    },
  },
  {
    id: 'projectsImpacted',
    accessorKey: 'projectsImpacted',
    header: 'Projects Impacted',
    cell: (info) => {
      const count = info.getValue<DataRefreshStatusRow['projectsImpacted']>();
      return <GridCellText>{count == null ? '—' : count.toLocaleString()}</GridCellText>;
    },
    enableSorting: true,
    enableColumnFilter: true,
    size: 160,
    meta: {
      filterValueConfig: { type: 'text' },
      filterOperatorTypeConfig: OPERATOR_TYPES.COMPARISON,
    },
  },
  {
    // Display-only: not sortable/filterable per the endpoint contract (free text maintained by
    // the CivilGrid team); the global search box covers notes.
    id: 'notes',
    accessorKey: 'notes',
    header: 'Notes',
    // A long note is clamped to the cell with an ellipsis (GridCellText) and shown in full on
    // hover — same tooltip treatment as the Public Notes column on the CivilSearch requests
    // table, which uses the same ServerTable. The min-w-0 wrapper is what lets the clamp
    // resolve against the cell width instead of the note's natural width.
    cell: (info) => {
      const notes = info.getValue<DataRefreshStatusRow['notes']>();
      if (!notes) return <GridCellText data-testid="data-refresh-status-notes-cell">—</GridCellText>;
      return (
        <div className="min-w-0 w-full">
          <Tooltip
            contentChildren={<span className="text-xs text-white whitespace-pre-wrap max-w-xs block">{notes}</span>}
          >
            <GridCellText data-testid="data-refresh-status-notes-cell">{notes}</GridCellText>
          </Tooltip>
        </div>
      );
    },
    enableSorting: false,
    enableColumnFilter: false,
    size: 420,
    // Opt out of the table's shared 800px maxSize cap so notes can be dragged wide enough to
    // fill the whitespace a large monitor leaves on this table.
    maxSize: Number.MAX_SAFE_INTEGER,
  },
];
